import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { NAV, viewDigest } from "../nexsys/data";
import { REASONING, ORCHESTRA_CADENCE } from "../components/ReasoningStream";

const ChattyContext = createContext(null);
export const useChatty = () => useContext(ChattyContext);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Voice/chat navigation — say a screen name to switch views.
const VIEW_ALIASES = [
  ["command", ["command center", "command centre", "command", "overview", "home", "main dashboard", "portfolio health screen"]],
  ["cognitive", ["cognitive core", "cognitive platform", "cognitive", "enterprise cognitive", "cognitive services", "agent orchestra", "agents", "edios", "scious"]],
  ["decisions", ["decision center", "decision centre", "decisions", "decision", "recommended actions", "recommendations"]],
  ["risk", ["risk monitoring", "risk monitor", "risk screen", "risks", "risk"]],
  ["timeline", ["timeline forecast", "timeline", "gantt", "program timeline", "schedule view", "schedule"]],
  ["twin", ["digital twin", "twin", "system topology", "topology", "dependency network"]],
  ["predictive", ["predictive analytics", "predictive", "predictions", "prediction", "scenario comparison", "scenarios"]],
  ["governance", ["governance", "compliance", "model registry"]],
  ["signals", ["ai signals", "signals", "signal stream"]],
  ["integrations", ["data sources", "integrations", "connectors", "integration", "source systems"]],
];
const NAV_TRIGGER = /\b(go to|open|show|switch to|switch over|navigate to|take me to|take me over|pull up|bring up|display|jump to|move to|go back to|let'?s see|let me see|let'?s look at|look at|load|see the|view the|view)\b/;

// Decision-intent — route these through the full agent orchestra (governed Decision Object).
const DECISION_INTENT = /\b(should we|should i|shall we|do we (approve|greenlight|proceed|go)|go\/?no-?go|make a decision|decide whether|what should (we|i) (do|decide)|what'?s the (best|right) (move|call|decision)|recommend|greenlight|reallocat|prioriti|approve the|worth (it|doing))\b/i;

function matchView(raw) {
  const t = (raw || "").toLowerCase().replace(/[.?!,]/g, " ").replace(/\s+/g, " ").trim();
  if (!t) return null;
  const words = t.split(/\s+/).length;
  const hasTrigger = NAV_TRIGGER.test(t);
  for (const [id, aliases] of VIEW_ALIASES) {
    for (const a of aliases) {
      const multiword = a.includes(" ");
      if (t === a || t === `${a} screen` || t === `${a} view` || t === `${a} tab` || t === `${a} page`) return id; // bare name
      if (multiword && t.includes(a) && (hasTrigger || words <= 7)) return id;       // "open the command center view"
      if (!multiword && hasTrigger && words <= 7) {
        const re = new RegExp(`\\b${a}\\b`);
        if (re.test(t)) return id;                                                    // "go to risk"
      }
    }
  }
  return null;
}
const GREETING = {
  role: "assistant",
  content: "Hi, I'm Chatty 👋 — your Nexis.team guide. Ask me about our services, industries, or how to navigate the NexSys Command Center.",
};

function getSessionId() {
  let id = localStorage.getItem("chatty_session");
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("chatty_session", id);
  }
  return id;
}

// Tiny silent clip used to "unlock" audio playback within a user gesture (iOS/Android).
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAIA+AAABAAgAZGF0YQAAAAA=";

function stripForSpeech(t) {
  return (t || "")
    .replace(/[*_`#>]/g, "")
    .replace(/^\s*[-•]\s*/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function ChattyProvider({ children }) {
  const location = useLocation();
  const isNexsys = location.pathname.startsWith("/nexsys");

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [conversation, setConversation] = useState(false);
  const [listenSignal, setListenSignal] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [briefing, setBriefing] = useState(false);
  const [orchestrating, setOrchestrating] = useState(false);
  const [lastDecision, setLastDecision] = useState(null);
  const [resumed, setResumed] = useState(null);
  const sessionId = useRef(getSessionId());
  const audioRef = useRef(null);
  const convoRef = useRef(false);
  useEffect(() => { convoRef.current = conversation; }, [conversation]);

  useEffect(() => {
    fetch(`${API}/chat/${sessionId.current}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.messages?.length) setMessages(d.messages.map((m) => ({ role: m.role, content: m.content })));
      })
      .catch(() => {});
  }, []);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setSpeaking(false);
  }, []);

  // Reuse ONE audio element and unlock it on a user gesture so async TTS can play on mobile.
  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = "auto";
      a.setAttribute("playsinline", "true");
      audioRef.current = a;
    }
    return audioRef.current;
  }, []);

  const unlockAudio = useCallback(() => {
    const a = getAudio();
    try {
      a.src = SILENT_WAV;
      const p = a.play();
      if (p && p.then) p.then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
    } catch { /* ignore */ }
  }, [getAudio]);

  const speak = useCallback(async (text) => {
    const clean = stripForSpeech(text);
    if (!clean) return;
    try {
      stopSpeaking();
      const r = await fetch(`${API}/voice/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: clean, voice: "onyx" }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      const audio = getAudio(); // reuse the gesture-unlocked element
      audio.onended = () => {
        setSpeaking(false);
        if (convoRef.current) setListenSignal((s) => s + 1);
      };
      audio.onerror = () => setSpeaking(false);
      audio.src = d.audio;
      setSpeaking(true);
      await audio.play().catch(() => setSpeaking(false));
    } catch {
      setSpeaking(false);
    }
  }, [stopSpeaking, getAudio, isNexsys]);

  const pushAssistant = useCallback((content, { spoken = true, ...extra } = {}) => {
    setMessages((m) => [...m, { role: "assistant", content, ...extra }]);
    if (spoken && voiceEnabled && (isNexsys || convoRef.current)) speak(content);
  }, [voiceEnabled, isNexsys, speak]);

  // Session expired / revoked → clear the stale token and prompt a fresh sign-in
  // (the gate's heartbeat then swaps the dashboard back to the login screen).
  const handleSessionExpired = useCallback(() => {
    localStorage.setItem("nexsys_gate_notice", "Your NEXSYS session expired for security. Please sign in again to continue.");
    localStorage.removeItem("nexsys_gate_token");
    window.dispatchEvent(new Event("nexsys-gate-change"));
  }, []);

  // Voice/chat navigation: "open Risk Monitoring" / "Digital Twin" → switch views.
  const navigateIfCommand = useCallback((raw) => {
    const id = matchView(raw);
    if (!id || typeof window.__nexsysSetView !== "function") return false;
    window.__nexsysSetView(id);
    const label = NAV.find((n) => n.id === id)?.label || id;
    const confirm = `Opening ${label}.`;
    setOpen(true);
    setMessages((m) => [...m, { role: "user", content: raw }, { role: "assistant", content: confirm }]);
    if (voiceEnabled && (isNexsys || convoRef.current)) speak(confirm);
    else if (convoRef.current) setListenSignal((s) => s + 1);
    return true;
  }, [voiceEnabled, isNexsys, speak]);

  // Grounded, voice-friendly NexSys answer (reads live portfolio data).
  const askNexsys = useCallback(async (text) => {
    const msg = (text || "").trim();
    if (!msg || busy) return;
    setOpen(true);
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/nexsys/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("nexsys_gate_token") ? { Authorization: `Bearer ${localStorage.getItem("nexsys_gate_token")}` } : {}) },
        body: JSON.stringify({ question: msg, view: window.__nexsysView || "", view_context: viewDigest(window.__nexsysView || ""), session_id: sessionId.current }),
      });
      if (r.status === 401) { handleSessionExpired(); return; }
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      pushAssistant(d.reply, { grounding: d.grounding?.views });
    } catch {
      pushAssistant("I couldn't reach the intelligence core just now. Please try again.", { spoken: false });
    } finally {
      setBusy(false);
    }
  }, [busy, pushAssistant, handleSessionExpired]);

  // Open the full governed Decision Object on the Cognitive Core screen.
  const openDecision = useCallback((d) => {
    if (!d) return;
    window.__nexsysPendingDecision = d;
    if (typeof window.__nexsysSetView === "function") window.__nexsysSetView("cognitive");
    setTimeout(() => { if (typeof window.__nexsysShowDecision === "function") window.__nexsysShowDecision(d); }, 80);
  }, []);

  // Decision question → run the full agent orchestra → governed Decision Object.
  const orchestrateQuestion = useCallback(async (text) => {
    const q = (text || "").trim();
    if (!q || orchestrating || busy) return;
    setOpen(true);
    setMessages((m) => [...m, { role: "user", content: q }]);
    setOrchestrating(true);
    const started = Date.now();
    try {
      const r = await fetch(`${API}/nexsys/orchestrate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("nexsys_gate_token") ? { Authorization: `Bearer ${localStorage.getItem("nexsys_gate_token")}` } : {}) },
        body: JSON.stringify({ question: q, view: window.__nexsysView || "", view_context: viewDigest(window.__nexsysView || ""), session_id: sessionId.current }),
      });
      if (r.status === 401) { handleSessionExpired(); return; }
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      const g = d.governance || {};
      const dec = d.decision || {};
      const verdict = g.allowed ? "governance approved" : "governance blocked";
      const recall = (d.recall || [])[0];
      const recallNote = recall ? `Similar to a prior decision — ${recall.title} (${recall.outcome || recall.status}). ` : "";
      const summary = `${recallNote}${dec.recommendation} (${verdict}, ${dec.confidence}% confidence).`;
      // Let the cognitive-substrate pipeline play through its stages before revealing the verdict.
      const pipelineMs = REASONING.orchestra.steps.length * ORCHESTRA_CADENCE;
      const hold = Math.max(0, pipelineMs - (Date.now() - started));
      await new Promise((res) => setTimeout(res, hold));
      setLastDecision(d);
      setMessages((m) => [...m, { role: "assistant", content: summary, decision: d }]);
      if (voiceEnabled && isNexsys) speak(summary);
      else if (convoRef.current && isNexsys) setListenSignal((s) => s + 1);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't complete the decision reasoning just now. Please try again." }]);
    } finally {
      setOrchestrating(false);
    }
  }, [orchestrating, busy, voiceEnabled, isNexsys, speak, handleSessionExpired]);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || "").trim();
    if (!msg || busy) return;
    if (isNexsys) {
      if (navigateIfCommand(msg)) return; // screen switch by name
      if (DECISION_INTENT.test(msg)) return orchestrateQuestion(msg); // full agent orchestra
      return askNexsys(msg); // grounded + spoken on the Command Center
    }
    setOpen(true);
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      pushAssistant(d.reply, { spoken: convoRef.current }); // speak + re-listen when hands-free
    } catch {
      pushAssistant("I'm having trouble connecting right now. Please try again, or email hello@nexis.team.", { spoken: false });
    } finally {
      setBusy(false);
    }
  }, [busy, isNexsys, askNexsys, pushAssistant, navigateIfCommand, orchestrateQuestion]);

  const sendGrounded = useCallback(async (displayText, fetcher) => {
    if (busy) return;
    setOpen(true);
    setMessages((m) => [...m, { role: "user", content: displayText }]);
    setBusy(true);
    try {
      const d = await fetcher();
      pushAssistant(d.reply);
    } catch {
      pushAssistant("The optimization engine is temporarily unavailable. Please try again.", { spoken: false });
    } finally {
      setBusy(false);
    }
  }, [busy, pushAssistant]);

  // Voice input: transcribe recorded audio, then answer (grounded + spoken).
  const askByVoice = useCallback(async (blob) => {
    if (busy || transcribing) return;
    setOpen(true);
    setTranscribing(true);
    let text = "";
    try {
      const fd = new FormData();
      fd.append("audio", blob, "speech.webm");
      const r = await fetch(`${API}/voice/transcribe`, { method: "POST", body: fd });
      if (r.ok) text = ((await r.json()).text || "").trim();
    } catch {
      /* fall through */
    } finally {
      setTranscribing(false);
    }
    if (!text) {
      if (convoRef.current) setListenSignal((s) => s + 1); // hands-free: keep listening
      else pushAssistant("I didn't catch that clearly — please try again.", { spoken: false });
      return;
    }
    if (navigateIfCommand(text)) return; // "open Risk Monitoring" by voice
    if (DECISION_INTENT.test(text)) return orchestrateQuestion(text); // spoken decision → orchestra
    askNexsys(text);
  }, [busy, transcribing, askNexsys, pushAssistant, navigateIfCommand, orchestrateQuestion]);

  const value = {
    open, setOpen, busy, messages, sendMessage, sendGrounded,
    isNexsys, voiceEnabled, setVoiceEnabled, speaking, speak, stopSpeaking, unlockAudio,
    askByVoice, transcribing,
    conversation, setConversation, listenSignal,
    briefing, orchestrating, openDecision, lastDecision, resumed,
  };

  // Proactive ops briefing when the Command Center opens (only once authenticated through the gate).
  const briefedRef = useRef(false);
  const [gateOk, setGateOk] = useState(!!localStorage.getItem("nexsys_gate_token"));
  useEffect(() => {
    const on = () => setGateOk(!!localStorage.getItem("nexsys_gate_token"));
    window.addEventListener("nexsys-gate-change", on);
    return () => window.removeEventListener("nexsys-gate-change", on);
  }, []);

  // Continuum: resume the governed entity's state from its CSO when an authorized session opens.
  useEffect(() => {
    if (!isNexsys || !gateOk) { setResumed(null); return; }
    const t = localStorage.getItem("nexsys_gate_token");
    fetch(`${API}/nexsys/continuum/resume`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setResumed(d && d.resumed ? d : null))
      .catch(() => setResumed(null));
  }, [isNexsys, gateOk]);
  useEffect(() => {
    if (!isNexsys || !gateOk) return;
    const t = setTimeout(async () => {
      if (briefedRef.current) return;
      briefedRef.current = true;
      setOpen(true);
      setBriefing(true); // show the live-synthesis trace while the brief is prepared
      const started = Date.now();
      try {
        const r = await fetch(`${API}/nexsys/briefing`, { headers: { ...(localStorage.getItem("nexsys_gate_token") ? { Authorization: `Bearer ${localStorage.getItem("nexsys_gate_token")}` } : {}) } });
        const d = await r.json();
        const hold = Math.max(0, 2500 - (Date.now() - started)); // let the reasoning steps play
        setTimeout(() => {
          setBriefing(false);
          if (!d.text) return;
          setMessages((m) => [...m, { role: "assistant", content: d.text }]);
          if (voiceEnabled) speak(d.text);
        }, hold);
      } catch {
        setBriefing(false);
      }
    }, 2600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNexsys, gateOk]);

  return <ChattyContext.Provider value={value}>{children}</ChattyContext.Provider>;
}
